/**
 * Phase: explosion（progress > 0）。
 *
 * spec §3.5 / §8 / wireframe L751 に従い、Unified Path2D による破片描画を
 * 行う。二相モデル後半相当。
 *
 * 2026-04 Phase C 修正ラウンド Step 2（層モデル + ハイブリッド clip）:
 * 旧実装は outer 全面を innerColor で塗り、さらに silhouette で clip してから
 * Surface を描いていたが、これは層モデルの Inner 層が silhouette の外にも
 * 漏れる誤りだった。正しい層モデルは Background（`renderer.ts` が担当）→
 * Inner（`renderer.ts` が担当、silhouette clip 下で innerColor + inner overlay）
 * → Surface（破片、silhouette で clip あり）→ Text。
 *
 * 2026-04 Phase F Step 4（3 スロット overlay）:
 * 旧版は本関数内に `fillInnerBacking` があり silhouette 内側を innerColor で
 * ベタ塗りしていたが、Phase F Step 4 で Inner 層全体（色 + Inner overlay）を
 * `renderer.ts` の `drawInnerLayer` に移管。本関数は **Surface 層のみ** を
 * 担当する。Inner 下敷きが事前に塗られている前提は維持（破片が動いて隙間が
 * 空いても Inner 色 + 画像が覗く、spec §3.3 の完了基準）。
 *
 * 2026-04 Phase E Step 4（PNG Overlay composite）:
 * Surface の塗りを「solid surfaceColor」から「surface + overlays.surface PNG の
 * composite canvas」に置き換える。合成の概念:
 * 1. Unified Path2D（破片群を結合）で clip を張る
 * 2. `drawImage(composite, 0, 0)` で破片形にくり抜かれた composite が転写される
 * つまり破片 1 枚 1 枚に Surface overlay が貼り付いた状態で飛散する。
 * composite=null のときは従来の `ctx.fill(surfacePath)` に戻る（overlay なし
 * の既存挙動維持）。
 *
 * 描画順（Phase F Step 4 以降、本関数の責務）:
 * 1. silhouette で clip を張った状態で全 real cell を progress で transform
 *    （centroid 基準 rotate + translate）
 * 2. 変位後 polygon を **Unified Path2D** に結合し:
 *    - composite=null → 1 回だけ surfaceColor で fill（spec §3.5 / D-15）
 *    - composite 非 null → per-fragment transform + clip + drawImage(composite)
 *      （Phase E Step 4 bug 修正後の紙破り表現）
 *
 * 重要:
 * - **Inner 下敷きは本関数の外で塗る**（`renderer.ts` の `drawInnerLayer`）。
 *   これにより Inner 層は色 + 画像の 2 step をまとめて管理でき、Surface 層の
 *   clip スコープと独立に描ける。
 * - **Surface にのみ clip**: `warpPolygonByCells` 強化で silhouette が破片群を
 *   幾何的に包むようになったが、稀に個別の高速破片が silhouette を超える瞬間
 *   がある。Surface fill だけに clip を張ることで、破片がはみ出して見える
 *   bug 1 の視覚的保険となる。Inner には clip をかけないので二重 clip による
 *   AA シーム再発は避けられる（Inner 層の clip は drawInnerLayer の save/restore
 *   スコープで閉じている）。
 * - **Unified Path2D**: 破片ごとに個別 `fill()` すると AA の alpha 端が
 *   合成されて白い線が見える（D-15 指摘）。1 本の Path2D に moveTo/lineTo/
 *   closePath を積んで `ctx.fill(path)` を 1 回だけ呼ぶことで境界のにじみを
 *   排除する。
 *
 * 設計参照:
 * - docs/happa-designtool-v1.1-spec.md §3.3 / §3.5 / §3.6 / §8
 * - wireframes/v0.10/wireframe.html L622（transformPoly）/ L751（renderExplosion）
 */

import { easeOutExpo } from '../../core/easing';
import type { Cell, Layout, Scene, Vec2 } from '../../core/types';
import type { AppState } from '../../state';

/**
 * cell.points を progress で変換する（spec §3.5 / wireframe L622 `transformPoly`）。
 *
 * 変換:
 * - `p = easeOutExpo(progress)`
 * - 各頂点を centroid 基準で `rot × p × 0.25` rad 回転
 * - 次に `[vx × p, vy × p]` 平行移動
 *
 * 回転を `p × 0.25` に抑えるのは wireframe の挙動一致（破片が派手に回りすぎ
 * ない抑制係数、OQ-03）。回転量は `cell.rot` 側の振幅（spec §3.7）と
 * 合わせて決まる。
 *
 * Phase G から `renderer.ts` も本関数を利用する（Inner outline 計算用の
 * displaced polygon を 1 箇所で生成するため export 化）。`fillUnifiedSurface`
 * 内の使い方と完全に同じ変換式である必要があり、同一関数を共有する形で
 * 重複実装を避ける。
 */
export function transformPoly(cell: Cell, progress: number): Vec2[] {
  const p = easeOutExpo(progress);
  const [cx, cy] = cell.centroid;
  const ox = cell.vx * p;
  const oy = cell.vy * p;
  const t = cell.rot * p * 0.25;
  const cosT = Math.cos(t);
  const sinT = Math.sin(t);
  return cell.points.map(([x, y]): Vec2 => {
    const x0 = x - cx;
    const y0 = y - cy;
    return [x0 * cosT - y0 * sinT + cx + ox, x0 * sinT + y0 * cosT + cy + oy];
  });
}

/**
 * polygon の頂点列を Path2D に moveTo/lineTo/closePath で積む。
 *
 * 頂点が 3 未満の場合は追加しない（退化入力の安全側）。closePath を自動で呼ぶ。
 */
function addPolyToPath(path: Path2D, poly: Vec2[]): void {
  if (poly.length < 3) return;
  const first = poly[0];
  if (!first) return;
  path.moveTo(first[0], first[1]);
  for (let i = 1; i < poly.length; i++) {
    const p = poly[i];
    if (!p) continue;
    path.lineTo(p[0], p[1]);
  }
  path.closePath();
}

/**
 * `ctx` 側に polygon を経路として積む（moveTo → lineTo...→ closePath）。
 *
 * `ctx.beginPath()` は呼び出し側の責任（fill 経路の起点管理のため）。
 * 頂点不足（< 3）は無視。
 */
function tracePolyOnCtx(ctx: CanvasRenderingContext2D, poly: Vec2[]): void {
  if (poly.length < 3) return;
  const first = poly[0];
  if (!first) return;
  ctx.moveTo(first[0], first[1]);
  for (let i = 1; i < poly.length; i++) {
    const p = poly[i];
    if (!p) continue;
    ctx.lineTo(p[0], p[1]);
  }
  ctx.closePath();
}

/**
 * Unified Path2D で全 cell を surfaceColor （or composite）で一括 fill する（spec §3.5）。
 *
 * 2026-04 Phase C 修正ラウンド Step 2 / Bug 1 修正 (ハイブリッド案):
 * Surface fill のみ silhouette で clip する。`warpPolygonByCells` の強化
 * （最近傍 → 影響圏内最大変位）で silhouette が破片群を幾何的に包むように
 * なったが、稀に progress の中盤で 1 個だけ高速な破片が silhouette 外へ
 * 飛び出るケースが残る。その視覚的保険として silhouette clip を復活させる。
 *
 * **Inner 下敷きには clip をかけない**（Phase F Step 4 で `renderer.ts` の
 * `drawInnerLayer` に移管、自前 save/restore スコープで独立して塗る）。
 * 二重の clip/fill で AA シームが再発するのを避けるため、Surface 側の clip
 * はここだけに閉じ込める。Inner 下敷きは silhouette をソリッドに塗るので
 * AA 端がきれいに消え、その上に Surface を clip 付きで重ねる構成。
 *
 * `Scene.cells` には phantom 由来の cell も含まれる（contract: Cell.isPhantom、
 * 2026-04 Phase C 修正ラウンド Step 1 で導入）。描画対象は **real cell のみ**
 * なので本関数内で `isPhantom === false` で filter する（spec §3.4 / types.ts
 * Scene コメント参照）。phantom cell を描画すると innerPoly の外側の白いリングが
 * 可視化されてしまう。
 *
 * 2026-04-21 Phase E Step 4 bug 修正（紙破り表現）:
 * 旧実装は「Unified Path2D（displaced 破片群）で clip → drawImage(composite,0,0)」
 * で各破片が画面原点の composite を窓越しに見せていた。結果として「composite
 * の上を破片窓がスキャンする」視覚効果になり、破片が動いても映る画像はその
 * 時々の新しい位置の画像だった（= 破片が PNG の上を滑るように動く）。
 *
 * 正しい表現（紙が破れて散らばる）は、各破片が元位置の画像を連れて動くこと。
 * 数学的には:
 *   canvas[S] = master[T_inv(S)]  （S = 新位置、T = 破片の変位+回転変換、
 *                                   master = DETONATE 時に焼いた 1 枚絵）
 * Canvas 2D の `ctx.drawImage(img, 0, 0)` を transform T 下で行うと
 * ちょうど `canvas[S] = img[T_inv(S)]` になる（drawImage は current transform を
 * 適用する）。そこで:
 *   1. 破片 1 つごとに save()
 *   2. translate(centroid+disp) rotate(cell.rot*p*0.25) translate(-centroid) で
 *      cell の per-fragment 変換を適用
 *   3. clip(cell.points) — 元座標のまま clip（current transform により
 *      実効的に変換後の窓になる）
 *   4. drawImage(master, 0, 0) — current transform により master が
 *      T_inv 経由で参照され、元位置の画像が破片の中に描画される
 *   5. restore()
 *
 * `composite === null`（overlay なし）のときは従来通り Unified Path2D +
 * 単色 fill を使う（AA シーム対策で 1 回 fill、overlay なしの既存挙動維持）。
 * overlay 有りの per-fragment 描画ではフレーム境界に 1px の AA seam が
 * 出る可能性があるが、破片が動いて離れていく過程では Inner 色の下敷きが
 * 見えるのみで視覚的に許容範囲。
 *
 * silhouette clip と per-cell clip の「二段 clip」になるが、save/restore で
 * 各 cell のスコープに閉じており、Inner 下敷き（renderer.ts 側の別 scope で
 * 描画済み）には影響しない。
 */
function fillUnifiedSurface(
  ctx: CanvasRenderingContext2D,
  silhouette: Vec2[],
  cells: ReadonlyArray<Cell>,
  progress: number,
  surfaceColor: string,
  composite: HTMLCanvasElement | null,
): void {
  if (silhouette.length < 3) return;

  ctx.save();
  // silhouette 内に clip（破片が最外輪郭を超えるのを視覚的に封じる）。
  // Inner 下敷きは renderer.ts の `drawInnerLayer` で独立 scope に描画済みなので、
  // この clip は Surface 描画にしか効かない（`ctx.restore` で解除される）。
  ctx.beginPath();
  tracePolyOnCtx(ctx, silhouette);
  ctx.clip();

  if (composite === null) {
    // ── 既存挙動: solid surface color fill（overlay なし） ───────
    // Unified Path2D で全破片を 1 つの path にまとめて 1 回 fill。
    // これにより破片間の AA シーム（白線）が出ない（spec §3.5 / D-15）。
    const surfacePath = new Path2D();
    for (const cell of cells) {
      if (cell.isPhantom) continue;
      const displaced = transformPoly(cell, progress);
      addPolyToPath(surfacePath, displaced);
    }
    ctx.fillStyle = surfaceColor;
    ctx.fill(surfacePath);
  } else {
    // ── overlay 有り: per-fragment transform + clip(元座標) + drawImage ──
    // 各破片が元位置の画像を連れて動く「紙破り」表現（Phase E Step 4 bug 修正）。
    drawFragmentsWithComposite(ctx, cells, progress, composite);
  }
  ctx.restore();
}

/**
 * 破片 1 つごとに変換＋クリップ＋composite 描画を行う（Phase E Step 4 bug 修正）。
 *
 * transformPoly と同じ変換式を ctx 上に積んで clip を元座標 `cell.points` で
 * 張る。これにより clip 窓は変換後の新位置になり、`drawImage(composite,0,0)` は
 * current transform によって master を「元位置の画像」として描画する。
 *
 * cell.rot は Phase E Step 1 cleanup 以降 0 固定なので回転成分は no-op だが、
 * 将来の再有効化や他の per-cell 回転要求に備えて transform コードは残す。
 */
function drawFragmentsWithComposite(
  ctx: CanvasRenderingContext2D,
  cells: ReadonlyArray<Cell>,
  progress: number,
  composite: HTMLCanvasElement,
): void {
  const p = easeOutExpo(progress);
  for (const cell of cells) {
    if (cell.isPhantom) continue;
    if (cell.points.length < 3) continue;
    const [cx, cy] = cell.centroid;
    const ox = cell.vx * p;
    const oy = cell.vy * p;
    const rot = cell.rot * p * 0.25;

    ctx.save();
    // Cell の per-fragment 変換（transformPoly と数学的に等価の ctx 変換版）:
    // translate(centroid + displacement) → rotate → translate(-centroid)
    ctx.translate(cx + ox, cy + oy);
    if (rot !== 0) ctx.rotate(rot);
    ctx.translate(-cx, -cy);

    // 元座標 cell.points で clip。current transform により実効窓は変換後の位置。
    ctx.beginPath();
    tracePolyOnCtx(ctx, cell.points);
    ctx.clip();

    // master composite を画面原点へ drawImage。current transform が適用される
    // ため、canvas[S] = master[T_inv(S)] となり、破片の中に「元位置の画像」が
    // 映る。master は DETONATE 時に焼いた 1 枚絵で、explosion 中は不変。
    ctx.drawImage(composite, 0, 0);
    ctx.restore();
  }
}

/**
 * 爆発進行中シーン（progress > 0）を 1 フレーム描画する。
 *
 * 呼び出し条件: `state.progress > 0`。0 の場合は `renderPreExplosion` を呼ぶ。
 * `ctx` のサイズは呼び出し側で `Layout.outerW/H` に同期済みである前提。
 * Background 層（`state.backgroundColor` / `backgroundTransparent`）は
 * `renderer.ts` が本関数より前に塗っている前提。
 *
 * 2026-04 silhouette リファクタ（OQ-14 / 60fps 保守の一環）:
 * warped silhouette の計算は `renderer.ts` で **1 回だけ**行い、本関数と
 * テキスト描画の両方に同じ結果を渡す。以前は本関数内で `subdividePolygon`
 * + `warpPolygonByCells` を呼んでいたが、テキストレイヤーでも同計算が
 * 必要になったため二重計算を排除した。
 *
 * 2026-04 Phase F Step 4（3 スロット overlay / Inner 層移管）:
 * 旧版は本関数内に Inner 下敷き塗り（`fillInnerBacking`）があったが、
 * Inner 層（色 + Inner overlay）は `renderer.ts` の `drawInnerLayer` に
 * 移管。本関数は Surface 層のみを担当する。Inner 下敷きが事前に塗られて
 * いる前提は維持（破片が動いて隙間が空いても Inner 色 + 画像が覗く、
 * spec §3.3 の完了基準）。
 *
 * 2026-04 Phase E Step 4（PNG Overlay composite）:
 * `composite` 引数が非 null なら、Surface fill を `drawImage(composite)` に
 * 置き換えて overlay を破片形に貼り込む。null なら solid surfaceColor fill
 * の既存挙動（overlay 無しの回帰ゼロを保証）。
 *
 * @param ctx        描画先の 2D コンテキスト。
 * @param _L         現在の Layout（Phase C 修正で全面 fill が不要になり未使用）。
 * @param scene      `buildScene` の結果（`innerPoly` + `cells`）。
 * @param state      現在の AppState（`progress` / 色 / 運動パラメータ）。
 * @param silhouette `renderer.ts` が計算済みの warped silhouette。
 *                   `subdividePolygon(scene.innerPoly, perEdge)` →
 *                   `warpPolygonByCells(…, progress)` の結果。
 * @param composite  Phase E Step 4: surface + overlay 合成済み canvas。
 *                   null なら solid surfaceColor fill にフォールバック。
 */
export function renderExplosion(
  ctx: CanvasRenderingContext2D,
  _L: Layout,
  scene: Scene,
  state: AppState,
  silhouette: Vec2[],
  composite: HTMLCanvasElement | null,
): void {
  // Surface 破片（Unified Path2D、silhouette clip あり — ハイブリッド案）。
  // Inner 下敷きは renderer.ts の drawInnerLayer が事前に塗っているため
  // 本関数では触らない（Phase F Step 4 で責務分離）。
  fillUnifiedSurface(ctx, silhouette, scene.cells, state.progress, state.surfaceColor, composite);
}
